import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  keycloak!: Keycloak;
  private _refreshIntervalId?: number;

  private _ready: Promise<boolean>;
  private _resolveReady!: (value: boolean) => void;

  constructor(private _router: Router) {
    this._ready = new Promise(resolve => {
      this._resolveReady = resolve;
    });
  }
  get ready(): Promise<boolean> {
    return this._ready;
  }

  async init(): Promise<boolean> {
    try {
      const keycloakConfig = environment.keycloak;
      if (!keycloakConfig) {
        console.error('[AuthService] Keycloak config missing');
        this._resolveReady(false);
        return false;
      }

      this.keycloak = new Keycloak({
        url: keycloakConfig.url,
        realm: keycloakConfig.realm,
        clientId: keycloakConfig.clientId
      });

      const authenticated = await this.keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
        checkLoginIframe: false,
        pkceMethod: 'S256'
      });
      if (authenticated) {
        this.startTokenRefreshLoop();
      }

      this._resolveReady(authenticated);
      return authenticated;
    } catch (err) {
      console.error('[AuthService] Init failed', err);
      this._resolveReady(false);
      return false;
    }
  }

  saveCurrentPathInSession() {
    // save full path + query + fragment
    const returnUrl = window.location.pathname + window.location.search + window.location.hash;
    sessionStorage.setItem('session_return_to', returnUrl);
  }
  retrieveCurrentPathFromSession() {
    const returnTo = sessionStorage.getItem('session_return_to') ?? '/client';
    sessionStorage.removeItem('session_return_to');
    return returnTo;
  }

  login() {
    const callback = `${window.location.origin}/auth-callback`;
    this.keycloak.login({
      redirectUri: callback
    });
  }

  async handleRedirectAfterLogin(): Promise<void> {
    const returnTo = this.retrieveCurrentPathFromSession();

    // navigate with router (replaceUrl avoids adding another history entry)
    this._router.navigateByUrl(returnTo, { replaceUrl: true });
  }

  logout(): void {
    // Remove tokens, user info, etc.
    sessionStorage.clear();
    localStorage.clear();
    this.clearAllCookies();
    if ('caches' in window) {
      caches.keys().then(function (names) {
        for (const name of names) {
          caches.delete(name);
        }
      });
    }
    // Use Keycloak logout with redirect to login, then force full reload
    const redirectUri = window.location.origin + '/login';
    this.keycloak.logout({ redirectUri });
    // As a fallback, force reload after a short delay (in case Keycloak does not redirect)
    setTimeout(() => {
      window.location.href = redirectUri;
    }, 1000);
  }

  isLoggedIn(): boolean {
    return !!this.keycloak.token && !this.keycloak.isTokenExpired();
  }

  getToken(): string | undefined {
    return this.keycloak?.token;
  }

  getUsername(): string | undefined {
    return this.keycloak.tokenParsed?.['name'];
  }

  getEmail(): string | undefined {
    return this.keycloak.tokenParsed?.['email'];
  }

  getFullName(): string | undefined {
    return this.keycloak.tokenParsed?.['given_name'] + ' ' + this.keycloak.tokenParsed?.['family_name'];
  }

  private startTokenRefreshLoop(): void {
    if (this._refreshIntervalId) {
      clearInterval(this._refreshIntervalId);
    }

    const refresh = () => {
      const exp = this.keycloak.tokenParsed?.exp;
      if (!exp) return;

      const expiresInMs = exp * 1000 - Date.now();
      const refreshThresholdMs = 60 * 1000;

      if (expiresInMs < refreshThresholdMs) {
        this.keycloak
          .updateToken(60)
          .then((refreshed: any) => {
            if (refreshed) {
              console.log('[Keycloak] Token refreshed');
            } else {
              console.log('[Keycloak] Token still valid');
            }
          })
          .catch(() => {
            console.warn('[Keycloak] Failed to refresh token, logging out');
            this.logout();
          });
      }
    };

    this._refreshIntervalId = window.setInterval(refresh, 100 * 1000);
  }

  clearAllCookies() {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    }
  }
}
